import styled from 'styled-components';
import {
  Chip as MUIChip,
  FormControl as MUIFormControl,
  InputLabel as MUILabel,
  MenuItem as MUIMenuItem,
  Select as MUISelect,
  FormHelperText as MUIFormHelperText,
} from '@mui/material';
import { CloseRounded } from '@mui/icons-material';

import { MultiSelectProps } from './Select.type';

const ChipWrapper = styled(MUIChip)`
  &.MuiChip-root {
    background-color: ${({ theme }) => `${theme.palette.secondary.main}`};
    border-radius: ${({ theme }) => `${theme.spacing(0.5)}`};
    color: ${({ theme }) => `${theme.palette.secondary.contrastText}`};
    flex-direction: row-reverse;
    height: ${({ theme }) => `${theme.spacing(3)}`};
    margin: ${({ theme }) => `${theme.spacing(0.5)} ${theme.spacing(1)} 0 0 `};
  }

  .MuiChip-deleteIcon {
    color: ${({ theme }) => `${theme.palette.text.primary}`};
    margin: 0 0 0 ${({ theme }) => `${theme.spacing(1)}`};

    &:hover,
    &:focus {
      color: ${({ theme }) => `${theme.palette.primary.main}`};
    }
  }

  .MuiChip-label {
    font-size: ${({ theme }) => theme.typography.button.fontSize};
    line-height: ${({ theme }) => theme.typography.button.lineHeight};
    padding: 0 ${({ theme }) => `${theme.spacing(0.5)}`};
  }
`;

const FormControlWrapper = styled(MUIFormControl)`
  .MuiInputBase-root {
    background-color: ${({ theme }) => theme.palette.background.paper};
    border: ${({ theme, error }) =>
      `${error ? 2 : 1} solid ${
        error ? theme.palette.error.main : theme.palette.grey[400]
      }`};
    border-radius: ${({ theme }) => `${theme.spacing(0.5)}`};
    height: ${({ theme }) => `${theme.spacing(7)}`};
  }

  .MuiInputBase-root:hover {
    border-color: ${({ theme }) => theme.palette.primary.dark};
  }

  .MuiSelect-select {
    margin-top: ${({ theme }) => theme.spacing(1)};

    &:focus {
      background-color: transparent;
    }
  }

  label + .MuiInput-formControl {
    margin-top: 0;
  }
`;

const SelectWrapper = styled(MUISelect)<MultiSelectProps>`
  width: 100%;

  .MuiSelect-icon {
    color: ${({ theme }) => `${theme.palette.text.primary}`};
    height: 100%;
    right: ${({ theme }) => `${theme.spacing(0.5)}`};
    /* Magic number for manually centering the icon vertically. */
    top: -1px;
  }

  .MuiSelect-selectMenu {
    color: ${({ theme }) => `${theme.palette.text.secondary}`};
    overflow: visible;
    padding-left: ${({ theme }) => `${theme.spacing(1)}`};
    white-space: normal;

    input {
      border: none;
    }
  }
`;

const MenuItemWrapper = styled(MUIMenuItem)`
  &.MuiListItem-root {
    &.Mui-selected {
      background-color: ${({ theme }) => `${theme.palette.grey[200]}`};
    }

    &:hover,
    &.Mui-selected:hover,
    &:focus,
    &.Mui-selected:focus {
      background-color: ${({ theme }) => theme.palette.primary.lighter};
    }
  }
`;

const LabelWrapper = styled(MUILabel)`
  z-index: 1;
  &.MuiFormLabel-root {
    &.Mui-focused {
      color: ${({ theme }) => `${theme.palette.text.primary}`};
    }
  }

  &[data-shrink='false'] {
    transform: ${({ theme }) =>
      `translate(${theme.spacing(1)}, ${theme.spacing(1.7)})`};
  }

  &.Mui-error {
    color: ${({ theme }) => theme.palette.text.primary};
  }

  &.MuiInputLabel-shrink {
    color: ${({ theme }) => `${theme.palette.text.primary}`};
    transform: ${({ theme }) =>
      `translate(${theme.spacing(1)}, ${theme.spacing(0.5)}) scale(0.75)`};
  }

  & .MuiInputLabel-asterisk {
    color: ${({ theme }) => `${theme.palette.error.main}`};
  }
`;

const CloseRoundedWrapper = styled(CloseRounded)`
  &.MuiChip-deleteIcon {
    height: ${({ theme }) => `${theme.spacing(2.25)}`};
    width: ${({ theme }) => `${theme.spacing(2.25)}`};
  }
`;

const FormHelperText = styled(MUIFormHelperText)`
  margin-top: 8px;
`;

const SelectValueWrapper = styled.div`
  align-items: center;
  display: flex;

  & svg {
    color: ${({ theme }) => `${theme.palette.grey[500]}`};
    margin-right: ${({ theme }) => `${theme.spacing(1)}`};
  }
`;

export {
  ChipWrapper,
  FormControlWrapper,
  SelectWrapper,
  MenuItemWrapper,
  LabelWrapper,
  CloseRoundedWrapper,
  FormHelperText,
  SelectValueWrapper,
};
